import { useEffect, useMemo, useState } from 'react'
import { formatHumanName } from '@medplum/core'
import type { Patient } from '@medplum/fhirtypes'
import { useMedplum } from '@medplum/react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Collapse,
  Divider,
  Group,
  Modal,
  MultiSelect,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import {
  IconAlertCircle,
  IconArrowLeft,
  IconAt,
  IconCheck,
  IconDownload,
  IconRefresh,
  IconSend,
  IconSignature,
} from '@tabler/icons-react'
import { USDateInput } from '@/components/ui/USDateInput'
import { config } from '@/config'
import { useClient } from '@/hooks/useClient'
import { patientChartCountsQueryKey } from '@/hooks/patientChartCountsQueryKey'
import { logError } from '@/utils/errorUtils'
import { usePatientSidePanel } from '@/pages/Patient/components/PatientSidePanelContext'
import { SignatureSection } from '../OrderTests/shared/SignatureSection'
import { useSignature } from '../OrderTests/shared/useSignature'
import { useCanOrder } from '../OrderTests/shared/useCanOrder'
import { useAutoPopulate } from '../OrderTests/shared/useAutoPopulate'
import { todayFormatted } from '../OrderTests/shared/formHelpers'
import { downloadPdfBlob } from '../OrderTests/pdf/pdfHelpers'
import { buildTimeOptions } from '@/pages/Schedule/utils'
import { generateLetterPdf } from './generateLetterPdf'
import { useLetterSigners } from './useLetterWorkflow'
import {
  LETTER_CATEGORY_ORDER,
  LETTER_TEMPLATES,
  buildInitialValues,
  joinMultiValue,
  letterDateBounds,
  isLetterFieldRequired,
  isLetterFieldVisible,
  letterFieldErrors,
  findLetterTemplate,
  formatLetterDate,
  formatLetterTime,
  missingRequiredFields,
  parseLetterTime,
  visibleLetterFields,
  splitMultiValue,
  type LetterField,
  type LetterFieldValues,
  type LetterTemplate,
} from './letterTemplates'

const textareaStyles = {
  input: {
    fontSize: '15px',
    lineHeight: 1.65,
    padding: '18px 20px',
  },
}

type Props = {
  opened: boolean
  onClose: () => void
  patient: Patient
  /**
   * Opens the modal in review mode for an existing signature request (PD-1407),
   * from the bell's deep-link or the Care Queue task. The provider reviews what the
   * coordinator prepared and signs it.
   */
  requestId?: string
}

export function CreateLetterModal({ opened, onClose, patient, requestId }: Props) {
  const medplum = useMedplum()
  const client = useClient()
  const queryClient = useQueryClient()
  const { emitPanelEvent } = usePatientSidePanel()
  const signature = useSignature()
  const { canOrder, loading: canOrderLoading } = useCanOrder()
  const { patientDefaults, providerDefaults } = useAutoPopulate(patient)

  const [template, setTemplate] = useState<LetterTemplate | undefined>()
  const [values, setValues] = useState<LetterFieldValues>({})
  // A required field complains once the practitioner has been near it: after they
  // edited it, or after a submit that could not go through. A letter that opens
  // with legitimately empty dates stays quiet until then (PD-1583).
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set())
  const [submitAttempted, setSubmitAttempted] = useState(false)
  // Wording dropped from the fields by a status change. Once the practitioner has
  // taken the body over, nothing rewrites it for them, so the letter can still be
  // carrying a sentence the new status contradicts (Greptile !1537).
  const [retiredValues, setRetiredValues] = useState<
    readonly { readonly field: string; readonly label: string; readonly text: string }[]
  >([])
  const [body, setBody] = useState('')
  // Once the practitioner edits the body by hand we stop regenerating it from the
  // fields, so their wording is never silently overwritten.
  const [bodyEdited, setBodyEdited] = useState(false)
  const [signatureDataUrl, setSignatureDataUrl] = useState('')
  const [signatureDate, setSignatureDate] = useState('')
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  // Practitioner's call whether the patient hears about this letter at all.
  const [sendEmail, setSendEmail] = useState(true)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  // Signature workflow: who is being asked to sign, and — in review mode — the
  // request being reviewed.
  const [assigneeId, setAssigneeId] = useState<string | null>(null)
  const [request, setRequest] = useState<Awaited<
    ReturnType<typeof client.letterSignatureRequestGet>
  > | null>(null)

  const patientName = patient.name?.[0] ? formatHumanName(patient.name[0]) : ''
  const patientEmail = patientDefaults.email

  const letterContext = useMemo(
    () => ({
      patientName,
      clinicName: providerDefaults.organizationName || 'our office',
    }),
    [patientName, providerDefaults.organizationName],
  )

  const reviewMode = Boolean(requestId)
  // The provider who has to sign gets the Approve & Sign button; everyone else —
  // the preparer, and the signer once it is signed — sees it read-only. There is no
  // "send it back" step: a provider who wants a change edits the letter, or asks the
  // preparer and comes back to it.
  const isReviewer =
    reviewMode && request?.viewerRole === 'signer' && request.status !== 'completed'
  // Every letter carries a provider's signature, so a care coordinator can only ever
  // request one — the same permission line as labs (`useCanOrder`). Until the role
  // lookup settles we assume the stricter path, so the wrong button never flashes.
  const mustRequestSignature = !reviewMode && (canOrderLoading || !canOrder)
  const { options: signerOptions, loading: signersLoading } = useLetterSigners(mustRequestSignature)
  // Whether the person at this modal is the one whose signature goes on the letter —
  // never the preparer, who is only ever requesting one.
  const signingHere = !mustRequestSignature && (!reviewMode || isReviewer)

  // Cross-field problems (an absence that ends before it starts, a visit date after
  // the absence, restrictions on an unrestricted clearance…), keyed by field name.
  const fieldErrors = useMemo(
    () => (template ? letterFieldErrors(template, values) : {}),
    [template, values],
  )

  // Keep the letter body in step with the fields until the practitioner takes it over.
  useEffect(() => {
    if (!template || bodyEdited) return
    setBody(template.buildBody(values, letterContext))
  }, [template, values, letterContext, bodyEdited])

  // Review mode: pull the prepared draft back so the provider signs the very text
  // the coordinator wrote, not a re-derivation of it.
  useEffect(() => {
    if (!opened || !requestId) return
    let cancelled = false
    void (async () => {
      try {
        const loaded = await client.letterSignatureRequestGet({ id: requestId })
        if (cancelled) return
        setRequest(loaded)
        setTemplate(findLetterTemplate(loaded.draft.templateId))
        setValues(loaded.draft.values)
        // Treat it as hand-edited so the fields can't rewrite the reviewed wording.
        setBodyEdited(true)
        setBody(loaded.draft.body)
        setSendEmail(Boolean(loaded.draft.email))
        setEmailSubject(loaded.draft.email?.subject ?? '')
        setEmailBody(loaded.draft.email?.body ?? '')
      } catch (err) {
        logError('Letters', 'Load letter signature request', err)
        showNotification({
          title: 'Error',
          message: 'Could not open that signature request',
          color: 'red',
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [opened, requestId, client])

  // Reuse the practitioner's stored signature, exactly as the order forms do.
  useEffect(() => {
    if (signature.savedSignature && !signatureDataUrl) {
      setSignatureDataUrl(signature.savedSignature)
      setSignatureDate(todayFormatted())
    }
  }, [signature.savedSignature, signatureDataUrl])

  function selectTemplate(next: LetterTemplate) {
    setTemplate(next)
    setValues(buildInitialValues(next, letterContext))
    setTouched(new Set())
    setSubmitAttempted(false)
    setRetiredValues([])
    setBodyEdited(false)
    setIsSaved(false)
    setSendEmail(true)
    setEmailSubject('A new document is available in your patient portal')
    setEmailBody(buildDefaultEmailBody(patientDefaults.firstName, providerDefaults.providerName))
  }

  function resetState() {
    setTemplate(undefined)
    setValues({})
    setTouched(new Set())
    setSubmitAttempted(false)
    setRetiredValues([])
    setBody('')
    setBodyEdited(false)
    setEmailSubject('')
    setEmailBody('')
    setSendEmail(true)
    setIsPreviewing(false)
    setIsSubmitting(false)
    setIsSaved(false)
    setAssigneeId(null)
    setRequest(null)
  }

  function handleClose() {
    resetState()
    onClose()
  }

  function setValue(name: string, value: string) {
    // Worked out here rather than inside a `setValues` updater: React only runs an
    // updater eagerly while its queue is empty, so reading what it dropped from the
    // outside is a coin toss under batching (Greptile !1537).
    const next = { ...values, [name]: value }
    const retired: { field: string; label: string; text: string }[] = []
    // Drop what the new value hides, so a status the practitioner has moved away
    // from cannot leave its restrictions behind in the letter (PD-1575).
    for (const field of template?.fields ?? []) {
      if (isLetterFieldVisible(field, next)) continue
      for (const text of letterBodyText(field, values[field.name] ?? '')) {
        retired.push({ field: field.name, label: field.label, text })
      }
      next[field.name] = ''
    }
    setValues(next)
    if (retired.length > 0) setRetiredValues((current) => [...current, ...retired])
    touch(name)
    setIsSaved(false)
  }

  async function buildPdf() {
    if (!template) throw new Error('No template selected')
    return generateLetterPdf({
      title: template.title,
      body,
      patientName,
      patientDateOfBirth: patientDefaults.dateOfBirth,
      provider: {
        organizationName: providerDefaults.organizationName,
        providerName: providerDefaults.providerName,
        npi: providerDefaults.npi,
        fax: providerDefaults.orgFax,
        address: providerDefaults.orgAddress,
        city: providerDefaults.orgCity,
        state: providerDefaults.orgState,
        zip: providerDefaults.orgZip,
      },
      signatureDataUrl,
      signatureDate,
      patientSignature: template.patientSignature,
    })
  }

  /** Blocks submit and says why. Returns true when the letter is ready to save. */
  function validate(): boolean {
    setSubmitAttempted(true)
    if (!template) return false

    // Everything that is empty, in one toast. Each of these used to be its own
    // gate, so the practitioner learned about the letter text, then the email
    // subject, then the email body across three separate submits (PD-1609).
    const missing = [...missingRequiredFields(template, values), ...emptyContentFields]
    if (missing.length > 0) {
      showNotification({
        title: 'Missing information',
        message: `Please fill in: ${missing.join(', ')}`,
        color: 'yellow',
      })
      return false
    }

    // Every format problem too — one per submit had the same flaw.
    const invalid = Object.values(fieldErrors)
    if (invalid.length > 0) {
      showNotification({
        title: 'Check the letter details',
        message: invalid.join(' '),
        color: 'yellow',
      })
      return false
    }

    // Only contradictions are left: the empty cases were named above. A letter
    // still quoting a value it no longer carries is wrong, not unfinished.
    const contradictions = Object.values(contentErrors)
    if (contradictions.length > 0) {
      showNotification({
        title: 'Check the letter text',
        message: contradictions.join(' '),
        color: 'yellow',
      })
      return false
    }

    // Only the person whose signature goes on the letter needs one. A coordinator
    // requesting a provider's signature is not signing anything, and an
    // administrative letter carries no signature at all.
    if (signingHere && !signatureDataUrl) {
      showNotification({
        title: 'Signature required',
        message: 'Please sign the letter before submitting.',
        color: 'yellow',
      })
      return false
    }

    return true
  }

  /** The prepared letter, as the provider will receive and sign it. */
  function currentDraft() {
    return {
      templateId: template!.id,
      title: template!.title,
      values,
      body,
      ...(shouldEmail && patientEmail ? { email: { subject: emailSubject, body: emailBody } } : {}),
    }
  }

  /** Refreshes the chart so a newly filed letter shows without a reload. */
  function refreshChart(documentReferenceId: string) {
    medplum.invalidateSearches('DocumentReference')
    medplum.invalidateSearches('Provenance')
    emitPanelEvent('progressNotes:refresh', {
      kind: 'reference',
      reference: `DocumentReference/${documentReferenceId}`,
    })
    if (patient.id) {
      void queryClient.invalidateQueries({ queryKey: patientChartCountsQueryKey(patient.id) })
    }
  }

  async function handleRequestSignature() {
    if (!template || !validate()) return
    if (!assigneeId) {
      showNotification({
        title: 'Choose a provider',
        message: 'Pick the provider who should review and sign this letter.',
        color: 'yellow',
      })
      return
    }

    setIsSubmitting(true)
    try {
      const result = await client.letterSignatureRequestCreate({
        patientId: patient.id!,
        assigneeId,
        draft: currentDraft(),
      })
      setIsSaved(true)
      showNotification({
        title: 'Sent for signature',
        message: result.emailed
          ? 'The provider has been notified in the app and by email.'
          : 'The provider has been notified in the app.',
        color: 'green',
      })
    } catch (err) {
      logError('Letters', 'Request letter signature', err)
      showNotification({
        title: 'Error',
        message: 'Could not send the letter for signature',
        color: 'red',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleApprove() {
    if (!template || !requestId || !validate()) return

    setIsSubmitting(true)
    try {
      // The PDF is rendered here, with THIS provider's signature on it — the whole
      // point of the round-trip is that the signature is applied by the signer.
      const pdf = await buildPdf()
      const result = await client.letterSignatureRequestApprove({
        id: requestId,
        pdfBase64: pdf.base64,
        fileName: pdf.fileName,
      })
      setIsSaved(true)
      if (result.documentReferenceId) refreshChart(result.documentReferenceId)
      showNotification({
        title: result.emailSent ? 'Signed & sent' : 'Letter signed',
        message: result.emailSent
          ? 'The letter was filed to the chart and the patient was emailed.'
          : `The letter was filed to the chart. ${result.emailSkipReason ?? ''}`.trim(),
        color: 'green',
      })
    } catch (err) {
      logError('Letters', 'Approve letter signature request', err)
      showNotification({ title: 'Error', message: 'Could not sign the letter', color: 'red' })
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handlePreview() {
    if (!template) return
    setIsPreviewing(true)
    try {
      const pdf = await buildPdf()
      downloadPdfBlob(new Blob([pdf.bytes], { type: 'application/pdf' }), pdf.fileName)
    } catch (err) {
      logError('Letters', 'Letter PDF preview', err)
      showNotification({ title: 'Error', message: 'Failed to build the letter PDF', color: 'red' })
    } finally {
      setIsPreviewing(false)
    }
  }

  async function handleSubmit() {
    if (!template || !validate()) return

    setIsSubmitting(true)
    try {
      const pdf = await buildPdf()
      const result = await client.createPatientLetter({
        patientId: patient.id!,
        templateId: template.id,
        title: template.title,
        pdfBase64: pdf.base64,
        fileName: pdf.fileName,
        // Omitted when the practitioner opted out, or when there's no address to
        // send to — either way the letter is still filed to the chart.
        email: shouldEmail && patientEmail ? { subject: emailSubject, body: emailBody } : undefined,
      })

      setIsSaved(true)
      // Make the letter appear in the chart without a page reload. All four steps
      // are needed (same set as saving patient instructions, PD-1303).
      refreshChart(result.documentReferenceId)

      showNotification({
        title: result.emailSent ? 'Letter saved & sent' : 'Letter saved',
        message: result.emailSent
          ? 'The letter was added to the patient documents and the patient was emailed.'
          : `The letter was added to the patient documents. ${result.emailSkipReason ?? ''}`.trim(),
        color: result.emailSent ? 'green' : 'yellow',
      })
    } catch (err) {
      logError('Letters', 'Create patient letter', err)
      showNotification({ title: 'Error', message: 'Failed to save the letter', color: 'red' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const isBusy = isSubmitting || isPreviewing
  // Care coordinators can compose and preview but not file — same rule as orders.
  // The server enforces it too; this only keeps the button honest.
  const isSubmitDisabled = isSaved || isBusy || canOrderLoading || !canOrder
  // An email needs both an address on file and the practitioner's say-so.
  const shouldEmail = sendEmail && Boolean(patientEmail)

  function touch(name: string) {
    setTouched((current) => (current.has(name) ? current : new Set(current).add(name)))
  }

  const showsRequired = (name: string) => submitAttempted || touched.has(name)

  /** "X is required" for every visible field the practitioner has left blank. */
  const requiredErrors = useMemo(() => {
    if (!template) return {} as Record<string, string>
    const errors: Record<string, string> = {}
    for (const field of visibleLetterFields(template, values)) {
      if (field.required && !values[field.name]?.trim()) {
        errors[field.name] = `${field.label} is required`
      }
    }
    return errors
  }, [template, values])

  // The wording is as required as any date on the form. Emptied, it files a letter
  // with a blank LETTER section and emails the patient "(no subject)" with nothing
  // in it (PD-1569). The notification fields only matter while one is being sent.
  const { contentErrors, emptyContentFields } = useMemo(() => {
    const errors: { body?: string; emailSubject?: string; emailBody?: string } = {}
    // The labels of whatever is merely EMPTY, kept apart from the messages: the
    // body's error is sometimes a contradiction rather than an omission, and only
    // omissions belong in the "Please fill in" list (PD-1609).
    const empty: string[] = []
    if (!body.trim()) {
      errors.body = 'Letter text is required'
      empty.push('Letter text')
    }
    // What the letter legitimately carries right now, per field and in total. A
    // phrase is not stale if the field it came from applies again, and two fields
    // can hold the same words, so a visible one saying them must not vouch for a
    // hidden one's line (Greptile !1537).
    const visible = template ? visibleLetterFields(template, values) : []
    const byField = new Map<string, ReadonlySet<string>>(
      visible.map((field) => [
        field.name,
        new Set(letterBodyText(field, values[field.name] ?? '')),
      ]),
    )
    const anyField = new Set(
      visible.flatMap((field) => letterBodyText(field, values[field.name] ?? '')),
    )
    // Only ever true for a hand-edited body: an unedited one is rebuilt from the
    // fields and cannot still be quoting a value they no longer hold.
    //
    // The retired field's own line is what makes a letter contradict itself, so
    // that is what is looked for. Falling back to the bare text would block a
    // letter the practitioner has already corrected: `Restrictions through` may
    // legitimately equal `Return to work effective`, and deleting the obsolete
    // line leaves that same printed date in a sentence that is perfectly valid
    // (Greptile !1546). The bare text still counts when NO visible field accounts
    // for it, which keeps a prose-rendered field covered.
    const stale = bodyEdited
      ? retiredValues.find(
          (retired) =>
            !byField.get(retired.field)?.has(retired.text) &&
            (body.includes(`${retired.label}: ${retired.text}`) ||
              (body.includes(retired.text) && !anyField.has(retired.text))),
        )
      : undefined
    if (stale) {
      errors.body = `The letter text still says “${stale.text}”, which no longer applies to this letter. Edit it or reset it from the fields.`
    }
    if (shouldEmail) {
      if (!emailSubject.trim()) {
        errors.emailSubject = 'Email subject is required'
        empty.push('Email subject')
      }
      if (!emailBody.trim()) {
        errors.emailBody = 'Email body is required'
        empty.push('Email body')
      }
    }
    return { contentErrors: errors, emptyContentFields: empty }
  }, [body, bodyEdited, retiredValues, template, values, shouldEmail, emailSubject, emailBody])

  const title = (
    <Stack gap={2}>
      <Text fw={700} size="xl" c="#121827">
        {template ? template.title : 'Create Letter'}
      </Text>
      <Text size="sm" c="#66738a" fw={400}>
        {template
          ? 'Review the letter, sign it, and choose what the patient is told.'
          : `Choose a letter template for ${patientName || 'this patient'}.`}
      </Text>
    </Stack>
  )

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={title}
      size="min(1180px, calc(100vw - 48px))"
      centered
      radius="lg"
      padding={0}
      styles={{
        header: { padding: '28px 32px', borderBottom: '1px solid var(--mantine-color-gray-2)' },
        body: { background: '#f8fafc' },
        close: { color: '#66738a' },
      }}
    >
      {!template ? (
        <TemplatePicker onSelect={selectTemplate} />
      ) : (
        <Stack gap={0}>
          {!patientEmail && (
            <Stack px={24} pt={20}>
              <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
                This patient has no email on file. The letter can still be saved to their chart, but
                no notification will be sent.
              </Alert>
            </Stack>
          )}

          <Stack p={24} gap="xl">
            {request && (
              <Alert icon={<IconSignature size={16} />} color="blue" variant="light">
                <Stack gap={6}>
                  <Text size="sm">
                    {isReviewer ? (
                      <>
                        <Text span fw={700} inherit>
                          {request.requestedBy}
                        </Text>{' '}
                        prepared this letter for {request.patientName} and asked for your signature.
                        Review it, edit anything that needs changing, then sign it.
                      </>
                    ) : request.status === 'completed' ? (
                      <>
                        This letter for {request.patientName} has been signed and filed to the
                        chart.
                      </>
                    ) : (
                      <>
                        You prepared this letter for {request.patientName}. It is waiting on{' '}
                        <Text span fw={700} inherit>
                          {request.assignedTo}
                        </Text>{' '}
                        to review and sign it — nothing reaches the chart until they do.
                      </>
                    )}
                  </Text>
                </Stack>
              </Alert>
            )}
            <Paper radius="md" p={22} withBorder bg="white">
              <Stack gap="lg">
                <Stack gap={4}>
                  <Text fw={700} size="lg" c="#172033">
                    Letter Details
                  </Text>
                  <Text size="sm" c="#526178" lh={1.55}>
                    {template.description} Patient name, date of birth, clinic details and your name
                    and credentials are filled in automatically.
                  </Text>
                </Stack>

                {/* One grid in declaration order, so the letter's fields read the way
                    the letter does. Free text and multi-selects span both columns —
                    their values are long enough that half a row cramps them. */}
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  {visibleLetterFields(template, values).map((field) => (
                    <div
                      key={field.name}
                      style={
                        field.kind === 'textarea' || field.kind === 'multiselect'
                          ? { gridColumn: '1 / -1' }
                          : undefined
                      }
                    >
                      <LetterFieldInput
                        field={field}
                        value={values[field.name] ?? ''}
                        values={values}
                        error={
                          fieldErrors[field.name] ??
                          (showsRequired(field.name) ? requiredErrors[field.name] : undefined)
                        }
                        hasLaterTimeField={template.fields.some((other) =>
                          other.rules?.some(
                            (rule) => rule.kind === 'timeAfter' && rule.field === field.name,
                          ),
                        )}
                        onChange={(value) => setValue(field.name, value)}
                      />
                    </div>
                  ))}
                </SimpleGrid>
              </Stack>
            </Paper>

            <Paper
              radius="md"
              p={22}
              withBorder
              style={{ borderColor: '#cfe1ff', background: '#eef5ff' }}
            >
              <Stack gap="lg">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Stack gap={4}>
                    <Group gap="sm" wrap="wrap">
                      <Text fw={700} size="lg" c="#172033">
                        Letter Text{' '}
                        <Text component="span" c="var(--mantine-color-error)" inherit>
                          *
                        </Text>
                      </Text>
                      {bodyEdited && (
                        <Badge variant="light" color="blue" radius="xl" tt="none">
                          Edited
                        </Badge>
                      )}
                    </Group>
                    <Text size="sm" c="#526178" lh={1.55}>
                      This is exactly what prints in the letter. Edit it freely — it stops updating
                      from the fields above once you do.
                    </Text>
                  </Stack>
                  {bodyEdited && (
                    <Button
                      variant="default"
                      size="xs"
                      leftSection={<IconRefresh size={14} />}
                      onClick={() => {
                        setBodyEdited(false)
                        setRetiredValues([])
                        setBody(template.buildBody(values, letterContext))
                      }}
                    >
                      Reset from template
                    </Button>
                  )}
                </Group>

                <Textarea
                  aria-label="Letter text"
                  value={body}
                  error={showsRequired('body') ? contentErrors.body : undefined}
                  onChange={(event) => {
                    setBody(event.currentTarget.value)
                    setBodyEdited(true)
                    touch('body')
                    setIsSaved(false)
                  }}
                  minRows={12}
                  maxRows={18}
                  autosize
                  radius="md"
                  styles={textareaStyles}
                />
              </Stack>
            </Paper>

            {!mustRequestSignature && !signingHere ? null : mustRequestSignature ? (
              <Paper radius="md" p={22} withBorder bg="white">
                <Stack gap="md">
                  <Group align="flex-start" gap="md" wrap="nowrap">
                    <IconSignature size={21} stroke={2.2} color="#334155" />
                    <Stack gap={6}>
                      <Text fw={700} size="lg" c="#172033">
                        Provider Signature Required
                      </Text>
                      <Text size="sm" c="#526178" lh={1.55}>
                        {template.title} states a medical opinion, so a provider has to review and
                        sign it. Choose who to send it to — they will be notified in the app and by
                        email, and the letter reaches the chart only once they sign.
                      </Text>
                    </Stack>
                  </Group>
                  <Select
                    label="Send to provider"
                    withAsterisk
                    size="md"
                    radius="md"
                    searchable
                    nothingFoundMessage={
                      signersLoading ? 'Loading providers…' : 'No providers available'
                    }
                    data={signerOptions}
                    value={assigneeId}
                    onChange={(next) => {
                      setAssigneeId(next)
                      setIsSaved(false)
                    }}
                  />
                </Stack>
              </Paper>
            ) : (
              <Paper radius="md" p={22} withBorder bg="white">
                <SignatureSection
                  signatureDataUrl={signatureDataUrl}
                  signatureDate={signatureDate}
                  savedSignature={signature.savedSignature}
                  signatureModalOpen={signature.signatureModalOpen}
                  onOpenModal={signature.openSignatureModal}
                  onCloseModal={signature.closeSignatureModal}
                  onConfirm={(dataUrl, date) => {
                    setSignatureDataUrl(dataUrl)
                    setSignatureDate(date)
                    setIsSaved(false)
                    signature.closeSignatureModal()
                  }}
                  onSaveSignature={signature.handleSaveSignature}
                  heading="PROVIDER SIGNATURE"
                  certificationText="I have reviewed this letter and it reflects my clinical assessment of this patient."
                />
              </Paper>
            )}

            {patientEmail && (
              <Paper radius="md" p={22} withBorder bg="white">
                <Stack gap={sendEmail ? 'lg' : 0}>
                  <Group align="flex-start" gap="md" wrap="nowrap">
                    <IconAt size={21} stroke={2.2} color={sendEmail ? '#334155' : '#adb5bd'} />
                    <Stack gap={6} style={{ flex: 1 }}>
                      <Group gap="sm" wrap="wrap" justify="space-between">
                        <Group gap="sm" wrap="wrap">
                          <Text fw={700} size="lg" c={sendEmail ? '#172033' : '#868e96'}>
                            Patient Email Notification
                          </Text>
                          <Badge variant="light" color="gray" radius="xl" tt="none">
                            {patientEmail}
                          </Badge>
                        </Group>
                        <Checkbox
                          label="Email the patient"
                          checked={sendEmail}
                          onChange={(event) => {
                            setSendEmail(event.currentTarget.checked)
                            setIsSaved(false)
                          }}
                        />
                      </Group>
                      <Text size="sm" c={sendEmail ? '#526178' : '#adb5bd'} lh={1.55}>
                        {sendEmail
                          ? 'The letter itself is not emailed. This message only tells the patient a new document is waiting in their portal.'
                          : 'No email will be sent. The letter is still saved to the patient chart.'}
                      </Text>
                    </Stack>
                  </Group>

                  <Collapse in={sendEmail}>
                    <Stack gap={8} pt="lg">
                      <TextInput
                        label="Email Subject"
                        withAsterisk
                        size="md"
                        radius="md"
                        value={emailSubject}
                        error={
                          showsRequired('emailSubject') ? contentErrors.emailSubject : undefined
                        }
                        onChange={(event) => {
                          setEmailSubject(event.currentTarget.value)
                          touch('emailSubject')
                          setIsSaved(false)
                        }}
                      />
                      <Textarea
                        label="Email Body"
                        withAsterisk
                        aria-label="Patient notification email"
                        value={emailBody}
                        error={showsRequired('emailBody') ? contentErrors.emailBody : undefined}
                        onChange={(event) => {
                          setEmailBody(event.currentTarget.value)
                          touch('emailBody')
                          setIsSaved(false)
                        }}
                        minRows={6}
                        maxRows={10}
                        autosize
                        radius="md"
                        styles={textareaStyles}
                      />
                    </Stack>
                  </Collapse>
                </Stack>
              </Paper>
            )}
          </Stack>

          <Divider />
          <Group justify="space-between" px={24} py={18} bg="white">
            <Group gap="sm">
              {!reviewMode && (
                <Button
                  size="md"
                  variant="default"
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={() => setTemplate(undefined)}
                  disabled={isBusy}
                >
                  Choose another letter
                </Button>
              )}
              <Button
                size="md"
                variant="default"
                leftSection={<IconDownload size={16} />}
                onClick={() => void handlePreview()}
                loading={isPreviewing}
                disabled={isSubmitting || (signingHere && !signatureDataUrl)}
              >
                Preview PDF
              </Button>
            </Group>

            {isReviewer ? (
              <Button
                size="md"
                variant="filled"
                color={isSaved ? 'green' : 'blue'}
                onClick={isSaved || isBusy ? undefined : () => void handleApprove()}
                loading={isSubmitting}
                leftSection={<IconCheck size={16} />}
                data-disabled={isSaved || isBusy || undefined}
              >
                {isSaved ? 'Done' : 'Approve & Sign'}
              </Button>
            ) : reviewMode ? (
              // Read-only: the preparer watching their request, or the signer looking
              // at a letter that is already filed.
              <Text size="sm" c="#526178" fw={500}>
                {!request
                  ? 'Loading the request…'
                  : request.status === 'completed'
                    ? 'Signed and filed to the chart'
                    : `Waiting on ${request.assignedTo || 'the provider'}`}
              </Text>
            ) : mustRequestSignature ? (
              <Button
                size="md"
                variant="filled"
                color={isSaved ? 'green' : 'blue'}
                onClick={isSaved || isBusy ? undefined : () => void handleRequestSignature()}
                loading={isSubmitting}
                leftSection={isSaved ? <IconCheck size={16} /> : <IconSignature size={16} />}
                data-disabled={isSaved || isBusy || undefined}
              >
                {isSaved ? 'Sent for Signature' : 'Request Provider Signature'}
              </Button>
            ) : (
              <Tooltip
                label="Practitioner permissions are required to submit a letter"
                disabled={canOrder || canOrderLoading}
              >
                <Button
                  size="md"
                  variant="filled"
                  color={isSaved ? 'green' : 'blue'}
                  onClick={isSubmitDisabled ? undefined : () => void handleSubmit()}
                  loading={isSubmitting}
                  leftSection={
                    isSaved || !shouldEmail ? <IconCheck size={16} /> : <IconSend size={16} />
                  }
                  data-disabled={isSubmitDisabled || undefined}
                >
                  {isSaved
                    ? 'Submitted'
                    : shouldEmail
                      ? 'Submit Letter & Send Email'
                      : 'Submit Letter'}
                </Button>
              </Tooltip>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  )
}

function TemplatePicker({ onSelect }: { onSelect: (template: LetterTemplate) => void }) {
  return (
    <Stack p={24} gap="xl">
      {LETTER_CATEGORY_ORDER.map((category) => {
        const templates = LETTER_TEMPLATES.filter((item) => item.category === category)
        if (templates.length === 0) return null

        return (
          <Stack key={category} gap="sm">
            <Text fw={700} size="sm" c="#526178" tt="uppercase">
              {category}
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {templates.map((template) => (
                <UnstyledButton key={template.id} onClick={() => onSelect(template)}>
                  <Paper
                    radius="md"
                    p="md"
                    withBorder
                    bg="white"
                    h="100%"
                    className="transition-colors hover:border-blue-400"
                  >
                    <Stack gap={6}>
                      <Text fw={700} size="sm" c="#172033">
                        {template.title}
                      </Text>
                      <Text size="xs" c="#66738a" lh={1.5}>
                        {template.description}
                      </Text>
                    </Stack>
                  </Paper>
                </UnstyledButton>
              ))}
            </SimpleGrid>
          </Stack>
        )
      })}
    </Stack>
  )
}

function LetterFieldInput({
  field,
  value,
  values,
  error,
  hasLaterTimeField,
  onChange,
}: {
  field: LetterField
  value: string
  /** The whole letter, so a date field can bound its calendar by its siblings. */
  values: LetterFieldValues
  error?: string
  /** Whether another field must hold a time later than this one. */
  hasLaterTimeField?: boolean
  onChange: (value: string) => void
}) {
  const label = field.label
  // A conditionally required field is marked as required while its condition holds,
  // so the asterisk matches the error the form is about to raise (PD-1575).
  const required = isLetterFieldRequired(field, values)

  switch (field.kind) {
    case 'date': {
      // Bound the picker by the same rules that produce the error, so an
      // out-of-order date is hard to pick and explained if it is typed anyway.
      const { minDate, maxDate } = letterDateBounds(field, values)
      return (
        <USDateInput
          label={label}
          withAsterisk={required}
          size="md"
          radius="md"
          value={value}
          error={error}
          minDate={minDate}
          maxDate={maxDate}
          onChange={(next) => onChange(next ?? '')}
        />
      )
    }
    case 'select':
      return (
        <Select
          label={label}
          withAsterisk={required}
          size="md"
          radius="md"
          data={[...(field.options ?? [])]}
          value={value || null}
          error={error}
          onChange={(next) => onChange(next ?? '')}
        />
      )
    case 'multiselect':
      return (
        <MultiSelect
          label={label}
          withAsterisk={required}
          size="md"
          radius="md"
          data={[...(field.options ?? [])]}
          value={splitMultiValue(value)}
          error={error}
          clearable
          searchable
          onChange={(next) => onChange(joinMultiValue(next))}
        />
      )
    case 'textarea':
      return (
        <Textarea
          label={label}
          withAsterisk={required}
          radius="md"
          autosize
          minRows={3}
          maxRows={6}
          placeholder={field.placeholder}
          value={value}
          error={error}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      )
    case 'time': {
      // A time another field has to come after cannot be the last slot of the day,
      // or that field has nothing left to pick and the letter can never be filed —
      // the Availability modal drops the final slot from its start select for the
      // same reason (Greptile !1537).
      const slots = hasLaterTimeField ? LETTER_TIME_OPTIONS.slice(0, -1) : LETTER_TIME_OPTIONS
      // Keep a time that is already on the letter selectable even if it falls
      // outside those slots, so opening a saved or reviewed draft never silently
      // drops the time it was written with.
      const options = !value || slots.includes(value) ? slots : [value, ...slots]
      // An end time can only follow its start, the way availability slots do.
      const after = field.rules?.find((rule) => rule.kind === 'timeAfter')
      const floor = after && 'field' in after ? parseLetterTime(values[after.field]) : null
      return (
        <Select
          label={label}
          withAsterisk={required}
          size="md"
          radius="md"
          placeholder="Select a time"
          data={options.map((time) => ({
            value: time,
            label: time,
            disabled: floor !== null && (parseLetterTime(time) ?? 0) <= floor,
          }))}
          value={value || null}
          error={error}
          onChange={(next) => onChange(next ?? '')}
        />
      )
    }
    case 'text':
    default:
      return (
        <TextInput
          label={label}
          withAsterisk={required}
          size="md"
          radius="md"
          placeholder={field.placeholder}
          value={value}
          error={error}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      )
  }
}

/**
 * How a field's value reads in the letter body — a date is printed long-form, not
 * as the ISO the field holds, so a raw comparison would never find it there.
 */
function letterBodyText(field: LetterField, value: string): string[] {
  const raw = value.trim()
  if (!raw) return []
  if (field.kind === 'date') return [formatLetterDate(raw)]
  if (field.kind === 'time') return [formatLetterTime(raw)]
  return [raw]
}

/**
 * The clinic's bookable half-hours, taken from the same helper the Schedule's
 * booking and availability modals use so the two can't drift apart (PD-1608).
 * A dropdown rather than free text, so a letter cannot state a time that is not
 * a time. Business hours are the right range here because the only template with
 * time fields verifies an appointment with one of our practitioners — which by
 * definition happened while we were open. (PD-1576 originally offered the whole
 * clock, for early draws and late visits.) Stored as the displayed label, which
 * is what the letter prints.
 */
const LETTER_TIME_OPTIONS = buildTimeOptions(8, 20, 30).map((option) => option.label)

function buildDefaultEmailBody(patientFirstName: string, providerName: string): string {
  const greeting = patientFirstName ? `Hi ${patientFirstName},` : 'Hello,'
  const portalLine = config.patientPortalUrl
    ? `Log in to view or download it: ${config.patientPortalUrl}`
    : 'Log in to your patient portal to view or download it.'

  return [
    greeting,
    'A new document from your care team is available in your beHuman patient portal.',
    portalLine,
    'Please reply to this email or call our office if you have any questions.',
    providerName || 'beHuman Health',
  ].join('\n\n')
}
